import { useState, useEffect } from 'react';
import toast, { Toaster } from 'react-hot-toast';
import { useParams, Link } from 'react-router-dom';

import type { PageContent } from '../../services/api';
import api from '../../services/api';

import BlogEditor from './BlogEditor';
import styles from './ContentEditor.module.css';
import IconRingEditor from './IconRingEditor';
import ImageGalleryEditor from './ImageGalleryEditor';
import ShopGalleryEditor from './ShopGalleryEditor';
import TextCloudEditor from './TextCloudEditor';

/**
 * ContentEditor Component
 *
 * Main router component that displays the appropriate content editor
 * based on the page type (text-cloud, icon-ring, image-gallery, shop-gallery)
 *
 * Features:
 * - Fetches page content by slug from route parameter
 * - Determines page type and shows corresponding editor
 * - Handles loading and error states
 * - Provides save functionality to child editors
 */

const ContentEditor = (): JSX.Element => {
  const { slug } = useParams<{ readonly slug: string }>();
  const [page, setPage] = useState<PageContent | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Fetch page content when slug changes
  useEffect(() => {
    if (!slug) {
      setError('No page slug provided');
      setLoading(false);
      return;
    }

    loadPageContent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  const loadPageContent = async (): Promise<void> => {
    if (!slug) return;

    setLoading(true);
    setError(null);

    try {
      const pageData = await api.content.getBySlug(slug);
      setPage(pageData);
    } catch (err) {
      const error = err as { readonly status?: number; readonly message?: string };
      console.error('Failed to load page content:', err);
      if (error.status === 404) {
        setError(`Page "${slug}" not found`);
      } else {
        setError(error.message ?? 'Failed to load page content');
      }
    } finally {
      setLoading(false);
    }
  };

  // Handle save from child editors
  const handleSave = async (content: Record<string, unknown>): Promise<void> => {
    if (!slug || !page) return;

    setSaving(true);

    try {
      const updatedPage = await api.content.update(slug, {
        content,
      });
      setPage(updatedPage);
      toast.success('Content saved successfully');
    } catch (err) {
      const error = err as { readonly message?: string };
      console.error('Failed to save content:', err);
      toast.error(error.message ?? 'Failed to save content');
      throw err; // Re-throw so child editors can handle it
    } finally {
      setSaving(false);
    }
  };

  // Render loading state
  if (loading) {
    return (
      <div className={styles.contentEditor}>
        <Toaster position="top-right" />
        <div className={styles.loadingContainer}>
          <div className={styles.spinner}></div>
          <p>Loading page content...</p>
        </div>
      </div>
    );
  }

  // Render error state
  if (error !== null || !page) {
    return (
      <div className={styles.contentEditor}>
        <Toaster position="top-right" />
        <div className={styles.errorContainer}>
          <div className={styles.errorIcon}>⚠️</div>
          <h2>Error Loading Content</h2>
          <p className={styles.errorMessage}>{error ?? 'Unknown error occurred'}</p>
          <div className={styles.errorActions}>
            <button onClick={loadPageContent} className={styles.retryButton}>
              🔄 Retry
            </button>
            <Link to="/admin/dashboard" className={styles.backButton}>
              ← Back to Dashboard
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Render appropriate editor based on page type
  const renderEditor = () => {
    switch (page.type) {
      case 'text-cloud':
        return (
          <TextCloudEditor
            slug={slug!}
            pageTitle={page.metadata?.title ?? slug!}
            initialContent={page.content}
            onSave={handleSave}
            isSaving={saving}
          />
        );

      case 'icon-ring':
        return (
          <IconRingEditor
            slug={slug!}
            pageTitle={page.metadata?.title ?? slug!}
            initialContent={page.content}
            onSave={handleSave}
            isSaving={saving}
          />
        );

      case 'image-gallery':
        return (
          <ImageGalleryEditor
            slug={slug!}
            pageTitle={page.metadata?.title ?? slug!}
            initialContent={page.content}
            onSave={handleSave}
            isSaving={saving}
          />
        );

      case 'shop-gallery':
        return (
          <ShopGalleryEditor
            slug={slug!}
            pageTitle={page.metadata?.title ?? slug!}
            initialContent={page.content}
            onSave={handleSave}
            isSaving={saving}
          />
        );

      case 'blog':
        return (
          <BlogEditor
            slug={slug!}
            pageTitle={page.metadata?.title ?? slug!}
            initialContent={page.content}
            onSave={handleSave}
            isSaving={saving}
          />
        );

      default:
        return (
          <div className={styles.errorContainer}>
            <div className={styles.errorIcon}>⚠️</div>
            <h2>Unknown Page Type</h2>
            <p className={styles.errorMessage}>Page type "{page.type}" is not supported</p>
            <Link to="/admin/dashboard" className={styles.backButton}>
              ← Back to Dashboard
            </Link>
          </div>
        );
    }
  };

  return (
    <div className={styles.contentEditor}>
      <Toaster position="top-right" />

      {/* Breadcrumb */}
      <div className={styles.breadcrumb}>
        <Link to="/admin/dashboard">Dashboard</Link>
        <span className={styles.separator}>›</span>
        <Link to="/admin/dashboard">Content</Link>
        <span className={styles.separator}>›</span>
        <span className={styles.currentPage}>{page.metadata?.title ?? slug}</span>
      </div>

      {/* Render appropriate editor */}
      {renderEditor()}
    </div>
  );
};

export default ContentEditor;
